import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { useAuth } from './AuthContext';
import { useHouseholdMembership } from './HouseholdMembershipContext';
import { navigateToSettings } from '../core/navigation/navigationRef';
import { buildClientFinanceContext } from '../services/buildClientFinanceContext';
import { subscribeDashboardRefresh } from '../services/dashboardRefreshBus';
import { telemetry } from '../services/telemetry';
import {
  buildPushedAssistantContext,
  createAssistantThread,
  getAssistantContext,
  getAssistantThread,
  getInsight,
  listAssistantThreads,
  putAssistantThreadContext,
  streamAssistantMessage,
  type AssistantMessage,
  type AssistantThread,
  type AssistantThreadSummary,
  type InsightCard,
} from '../services/assistantService';

const ACTIVE_THREAD_KEY = '@moneylens/assistant.activeThreadId';

type OpenAssistantOpts = {
  insightId?: string;
  /** In-memory card (avoids GET /insights/{id} when already loaded). */
  insight?: InsightCard;
  starterPrompt?: string;
  insightTitle?: string;
  widget?: string;
};

type AssistantContextValue = {
  open: boolean;
  minimized: boolean;
  busy: boolean;
  streaming: boolean;
  /** True while fetching spend, portfolio, home loan, and profile for grounding. */
  loadingContext: boolean;
  error: string | null;
  thread: AssistantThread | null;
  threads: AssistantThreadSummary[];
  historyOpen: boolean;
  pinnedInsightTitle: string | null;
  /** False → chat shell opens with upgrade CTA; streaming requires Pro. */
  canChat: boolean;
  openAssistant: (opts?: OpenAssistantOpts) => Promise<void>;
  closeAssistant: () => void;
  minimizeAssistant: () => void;
  expandAssistant: () => void;
  sendMessage: (content: string) => Promise<void>;
  startFresh: () => Promise<void>;
  resumeThread: (threadId: string) => Promise<void>;
  refreshThreadList: () => Promise<void>;
  setHistoryOpen: (open: boolean) => void;
};

const AssistantContext = createContext<AssistantContextValue | null>(null);

function visibleMessages(messages: AssistantMessage[] | undefined): AssistantMessage[] {
  return (messages ?? []).filter((m) => m.role === 'user' || m.role === 'assistant');
}

export function AssistantProvider({ children }: PropsWithChildren) {
  const { user, tokens, isAuthenticated } = useAuth();
  const { view } = useHouseholdMembership();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [loadingContext, setLoadingContext] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thread, setThread] = useState<AssistantThread | null>(null);
  const [threads, setThreads] = useState<AssistantThreadSummary[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pinnedInsightTitle, setPinnedInsightTitle] = useState<string | null>(null);
  const threadRef = useRef<AssistantThread | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  const hydratedRef = useRef(false);
  const snapshotInflightRef = useRef<Promise<Record<string, unknown>> | null>(null);
  /** Bumps when finance context should be re-pushed after import. */
  const contextEpochRef = useRef(0);
  const threadContextEpochRef = useRef<Map<string, number>>(new Map());

  const isPro = user?.plan === 'pro';

  useEffect(() => {
    threadRef.current = thread;
  }, [thread]);

  const bumpFinanceContext = useCallback((reason?: string) => {
    snapshotInflightRef.current = null;
    contextEpochRef.current += 1;
    if (reason) {
      console.info('[Assistant] ♻️ Refresh finance context', { reason });
    }
  }, []);

  useEffect(() => {
    return subscribeDashboardRefresh((generation) => {
      bumpFinanceContext(`dashboard_refresh:${generation}`);
    });
  }, [bumpFinanceContext]);

  useEffect(() => {
    bumpFinanceContext(isAuthenticated ? 'user_or_view' : 'logout');
    threadContextEpochRef.current.clear();
  }, [
    bumpFinanceContext,
    isAuthenticated,
    user?.id,
    user?.householdId,
    user?.meta?.monthlyIncome,
    user?.meta?.birthYear,
    user?.meta?.retirementAge,
    user?.meta?.assumedReturnPct,
    view,
  ]);

  const persistActiveThreadId = useCallback(async (threadId: string | null) => {
    try {
      if (threadId) {
        await AsyncStorage.setItem(ACTIVE_THREAD_KEY, threadId);
      } else {
        await AsyncStorage.removeItem(ACTIVE_THREAD_KEY);
      }
    } catch {
      // ignore storage failures
    }
  }, []);

  const refreshThreadList = useCallback(async () => {
    if (!isAuthenticated || !isPro) {
      setThreads([]);
      return;
    }
    try {
      const res = await listAssistantThreads();
      setThreads(res.threads ?? []);
    } catch {
      // history is best-effort
    }
  }, [isAuthenticated, isPro]);

  const applyThread = useCallback(
    async (next: AssistantThread | null) => {
      setThread(next);
      threadRef.current = next;
      await persistActiveThreadId(next?.id ?? null);
    },
    [persistActiveThreadId],
  );

  /**
   * Load finance snapshot from the backend (no on-device cache).
   */
  const loadFinanceSnapshot = useCallback(async (): Promise<Record<string, unknown>> => {
    if (!user?.id) {
      throw new Error(
        'Could not load your finances. Check your connection and try again.',
      );
    }

    if (snapshotInflightRef.current) {
      return snapshotInflightRef.current;
    }

    const inflight = (async () => {
      let snapshot: Record<string, unknown> | undefined;
      try {
        const res = await getAssistantContext(view);
        snapshot = res.context;
      } catch {
        snapshot = undefined;
      }

      if (!snapshot && tokens?.accessToken) {
        snapshot = await buildClientFinanceContext({
          accessToken: tokens.accessToken,
          userId: user.id,
          view,
          monthlyIncome: user.meta?.monthlyIncome,
          plan: user.plan,
          name: user.name,
          birthYear: user.meta?.birthYear,
          retirementAge: user.meta?.retirementAge,
          assumedReturnPct: user.meta?.assumedReturnPct,
        });
      }

      if (!snapshot) {
        throw new Error(
          'Could not load your finances. Check your connection and try again.',
        );
      }

      // Merge multi-month client slice when available (months + portfolio + loan + profile).
      if (tokens?.accessToken) {
        try {
          const clientSlice = await buildClientFinanceContext({
            accessToken: tokens.accessToken,
            userId: user.id,
            view,
            monthlyIncome: user.meta?.monthlyIncome,
            plan: user.plan,
            name: user.name,
            birthYear: user.meta?.birthYear,
            retirementAge: user.meta?.retirementAge,
            assumedReturnPct: user.meta?.assumedReturnPct,
          });
          snapshot = {
            ...snapshot,
            months: clientSlice.months,
            analysisMonth: clientSlice.analysisMonth,
            portfolio: clientSlice.portfolio ?? snapshot.portfolio,
            homeLoan: clientSlice.homeLoan ?? snapshot.homeLoan,
            household: clientSlice.household ?? snapshot.household,
            profile: {
              ...(typeof snapshot.profile === 'object' && snapshot.profile
                ? (snapshot.profile as Record<string, unknown>)
                : {}),
              ...(typeof clientSlice.profile === 'object' && clientSlice.profile
                ? (clientSlice.profile as Record<string, unknown>)
                : {}),
            },
            dataQuality: {
              ...(typeof snapshot.dataQuality === 'object' && snapshot.dataQuality
                ? (snapshot.dataQuality as Record<string, unknown>)
                : {}),
              ...(clientSlice.dataQuality as Record<string, unknown>),
            },
          };
        } catch {
          // server snapshot alone is still usable
        }
      }

      return snapshot;
    })();

    snapshotInflightRef.current = inflight;
    try {
      return await inflight;
    } finally {
      if (snapshotInflightRef.current === inflight) {
        snapshotInflightRef.current = null;
      }
    }
  }, [
    tokens?.accessToken,
    user?.id,
    user?.name,
    user?.meta?.monthlyIncome,
    user?.meta?.birthYear,
    user?.meta?.retirementAge,
    user?.meta?.assumedReturnPct,
    user?.plan,
    view,
  ]);

  /**
   * Build push blob for ai-assistant from a fresh backend snapshot.
   */
  const resolvePushContext = useCallback(
    async (opts?: {
      insightId?: string;
      insight?: InsightCard;
    }): Promise<{
      context: Record<string, unknown>;
      insight: InsightCard | null;
    }> => {
      let insight: InsightCard | null = opts?.insight ?? null;
      const needsFullInsight =
        Boolean(opts?.insightId) && (!insight || insight.contextPayload == null);
      if (needsFullInsight && opts?.insightId) {
        try {
          const res = await getInsight(opts.insightId, view);
          insight = res.insight;
        } catch {
          // keep partial in-memory card if present
        }
      }

      const snapshot = await loadFinanceSnapshot();
      return {
        context: buildPushedAssistantContext({ snapshot, insight }),
        insight,
      };
    },
    [loadFinanceSnapshot, view],
  );

  const markThreadContextEpoch = useCallback((threadId: string) => {
    threadContextEpochRef.current.set(threadId, contextEpochRef.current);
  }, []);

  const threadNeedsFreshContext = useCallback((threadId: string, hasContext?: boolean) => {
    if (!hasContext) return true;
    const attached = threadContextEpochRef.current.get(threadId) ?? 0;
    return attached < contextEpochRef.current;
  }, []);

  const streamIntoThread = useCallback(
    async (
      threadId: string,
      content: string,
      opts?: { context?: string | Record<string, unknown> },
    ) => {
      streamAbortRef.current?.abort();
      const abort = new AbortController();
      streamAbortRef.current = abort;

      const optimisticUser: AssistantMessage = {
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      };
      const streamingAssistant: AssistantMessage = {
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
      };

      setThread((prev) => {
        if (!prev || prev.id !== threadId) {
          return prev;
        }
        return {
          ...prev,
          messages: [...visibleMessages(prev.messages), optimisticUser, streamingAssistant],
          hasContext: prev.hasContext || Boolean(opts?.context),
        };
      });
      setStreaming(true);

      try {
        const result = await streamAssistantMessage(threadId, content, {
          signal: abort.signal,
          context: opts?.context,
          onDelta: (delta) => {
            setThread((prev) => {
              if (!prev || prev.id !== threadId) return prev;
              const msgs = [...prev.messages];
              for (let i = msgs.length - 1; i >= 0; i -= 1) {
                if (msgs[i].role === 'assistant') {
                  msgs[i] = { ...msgs[i], content: msgs[i].content + delta };
                  break;
                }
              }
              return { ...prev, messages: msgs };
            });
          },
        });

        if (abort.signal.aborted) return;

        setThread((prev) => {
          if (!prev || prev.id !== threadId) return prev;
          const msgs = [...prev.messages];
          for (let i = msgs.length - 1; i >= 0; i -= 1) {
            if (msgs[i].role === 'assistant') {
              msgs[i] = {
                ...msgs[i],
                content: result.content || msgs[i].content,
                ...(result.messageId ? { id: result.messageId } : {}),
              };
              break;
            }
          }
          const title = prev.title || content.slice(0, 80) || prev.title;
          return {
            ...prev,
            title,
            messages: msgs,
            updatedAt: new Date().toISOString(),
            hasContext: prev.hasContext || Boolean(opts?.context),
          };
        });
        await refreshThreadList();
      } catch (err) {
        if (abort.signal.aborted) return;
        throw err;
      } finally {
        if (streamAbortRef.current === abort) {
          streamAbortRef.current = null;
        }
        setStreaming(false);
      }
    },
    [refreshThreadList],
  );

  // Resume last thread after login (FAB close does not clear selection).
  useEffect(() => {
    if (!isAuthenticated || !isPro || hydratedRef.current) return;
    hydratedRef.current = true;
    void (async () => {
      try {
        const savedId = await AsyncStorage.getItem(ACTIVE_THREAD_KEY);
        await refreshThreadList();
        if (!savedId) return;
        const { thread: next } = await getAssistantThread(savedId);
        await applyThread(next);
      } catch {
        await persistActiveThreadId(null);
      }
    })();
  }, [applyThread, isAuthenticated, isPro, persistActiveThreadId, refreshThreadList]);

  useEffect(() => {
    if (!isAuthenticated) {
      hydratedRef.current = false;
      streamAbortRef.current?.abort();
      setThread(null);
      setThreads([]);
      setOpen(false);
      setMinimized(false);
      setHistoryOpen(false);
      setPinnedInsightTitle(null);
      void persistActiveThreadId(null);
    }
  }, [isAuthenticated, persistActiveThreadId]);

  const openAssistant = useCallback(
    async (opts?: OpenAssistantOpts) => {
      if (!isAuthenticated) {
        navigateToSettings();
        return;
      }

      void telemetry.logFeature('assistant_open', {
        entry: opts?.insightId || opts?.insight ? 'insight' : opts?.starterPrompt ? 'starter' : 'fab',
      });
      // Always open the chat shell — free users see an upgrade CTA instead of a dead click.
      setOpen(true);
      setMinimized(false);
      setError(null);
      setHistoryOpen(false);
      setPinnedInsightTitle(opts?.insightTitle ?? opts?.insight?.title ?? null);

      if (!isPro) {
        return;
      }

      // Reopen FAB: keep current chat (or empty composer). Create on send / New / insight.
      if (!opts?.insightId && !opts?.insight && !opts?.starterPrompt) {
        void refreshThreadList();
        return;
      }

      setBusy(true);
      setLoadingContext(true);
      try {
        const insightId = opts?.insightId ?? opts?.insight?.id;
        const { context: pushContext, insight } = await resolvePushContext({
          insightId,
          insight: opts?.insight,
        });
        setLoadingContext(false);

        const title =
          opts?.insightTitle ??
          insight?.title ??
          opts?.insight?.title ??
          opts?.starterPrompt?.slice(0, 80);

        const { thread: next } = await createAssistantThread({
          title,
          context: pushContext,
          metadata: {
            view,
            ...(insightId ? { sourceInsightId: insightId } : {}),
            ...(opts?.widget ? { widget: opts.widget } : {}),
          },
        });
        await applyThread({ ...next, hasContext: true });
        markThreadContextEpoch(next.id);
        void refreshThreadList();

        const starter =
          insight?.ctaPrompt?.trim() ||
          opts?.starterPrompt?.trim() ||
          opts?.insight?.ctaPrompt?.trim() ||
          undefined;
        if (starter) {
          setBusy(false);
          // Re-attach context on the stream turn so the model always sees it
          // (snapshot + insight.contextPayload from getInsight).
          await streamIntoThread(next.id, starter, { context: pushContext });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not start chat');
      } finally {
        setLoadingContext(false);
        setBusy(false);
      }
    },
    [
      applyThread,
      isAuthenticated,
      isPro,
      markThreadContextEpoch,
      refreshThreadList,
      resolvePushContext,
      streamIntoThread,
      view,
    ],
  );

  const closeAssistant = useCallback(() => {
    setOpen(false);
    setMinimized(false);
    setHistoryOpen(false);
  }, []);

  const minimizeAssistant = useCallback(() => {
    setMinimized(true);
    setHistoryOpen(false);
  }, []);

  const expandAssistant = useCallback(() => {
    setMinimized(false);
    setOpen(true);
  }, []);

  const startFresh = useCallback(async () => {
    if (!isPro) {
      navigateToSettings();
      return;
    }
    streamAbortRef.current?.abort();
    setError(null);
    setPinnedInsightTitle(null);
    setHistoryOpen(false);
    setBusy(true);
    setLoadingContext(true);
    try {
      const { context: pushContext } = await resolvePushContext();
      setLoadingContext(false);
      const { thread: next } = await createAssistantThread({
        context: pushContext,
        metadata: { view },
      });
      await applyThread({ ...next, hasContext: true });
      markThreadContextEpoch(next.id);
      void refreshThreadList();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start chat');
      await applyThread(null);
    } finally {
      setLoadingContext(false);
      setBusy(false);
    }
  }, [applyThread, isPro, markThreadContextEpoch, refreshThreadList, resolvePushContext, view]);

  const resumeThread = useCallback(
    async (threadId: string) => {
      if (!isAuthenticated) {
        navigateToSettings();
        return;
      }
      setOpen(true);
      setMinimized(false);
      setHistoryOpen(false);
      setError(null);
      setPinnedInsightTitle(null);
      if (!isPro) {
        return;
      }
      streamAbortRef.current?.abort();
      setBusy(true);
      try {
        const { thread: next } = await getAssistantThread(threadId);
        await applyThread(next);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not open chat');
      } finally {
        setBusy(false);
      }
    },
    [applyThread, isAuthenticated, isPro],
  );

  const sendMessage = useCallback(
    async (content: string) => {
      const trimmed = content.trim();
      if (!trimmed || streaming) return;
      if (!isPro) {
        navigateToSettings();
        return;
      }
      setBusy(true);
      setError(null);
      try {
        let active = threadRef.current;
        let turnContext: Record<string, unknown> | undefined;

        const needsSnapshot =
          !active || threadNeedsFreshContext(active.id, active.hasContext);

        if (!active) {
          setLoadingContext(true);
          const { context: pushContext } = await resolvePushContext();
          setLoadingContext(false);
          turnContext = pushContext;
          const { thread: next } = await createAssistantThread({
            context: pushContext,
            metadata: { view },
          });
          await applyThread({ ...next, hasContext: true });
          markThreadContextEpoch(next.id);
          active = next;
        } else if (needsSnapshot) {
          setLoadingContext(true);
          const resolved = await resolvePushContext();
          setLoadingContext(false);
          turnContext = resolved.context;
          try {
            await putAssistantThreadContext(active.id, turnContext);
            await applyThread({ ...active, hasContext: true });
            markThreadContextEpoch(active.id);
          } catch {
            // stream still accepts context inline
            markThreadContextEpoch(active.id);
          }
        }

        setBusy(false);
        await streamIntoThread(active.id, trimmed, {
          context: turnContext,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Message failed');
        setBusy(false);
        setLoadingContext(false);
        setStreaming(false);
      }
    },
    [
      applyThread,
      isPro,
      markThreadContextEpoch,
      resolvePushContext,
      streamIntoThread,
      streaming,
      threadNeedsFreshContext,
      view,
    ],
  );

  const value = useMemo(
    () => ({
      open,
      minimized,
      busy,
      streaming,
      loadingContext,
      error,
      thread,
      threads,
      historyOpen,
      pinnedInsightTitle,
      canChat: isPro,
      openAssistant,
      closeAssistant,
      minimizeAssistant,
      expandAssistant,
      sendMessage,
      startFresh,
      resumeThread,
      refreshThreadList,
      setHistoryOpen,
    }),
    [
      open,
      minimized,
      busy,
      streaming,
      loadingContext,
      error,
      thread,
      threads,
      historyOpen,
      pinnedInsightTitle,
      isPro,
      openAssistant,
      closeAssistant,
      minimizeAssistant,
      expandAssistant,
      sendMessage,
      startFresh,
      resumeThread,
      refreshThreadList,
    ],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext);
  if (!ctx) {
    throw new Error('useAssistant must be used within AssistantProvider');
  }
  return ctx;
}
